import { http } from '@/plugins/http'
import { normalize, schema } from 'normalizr'
import {
  RECEIVE_BANKS,
  RECEIVING_BANKS,
  RECEIVE_BANK_METADATA,
  RECEIVE_BANKS_FAILURE,
  SAVING_BANK,
  SAVING_BANK_FAILURE,
  UPDATING_BANK,
  UPDATING_BANK_FAILURE,
  DELETE_BANK,
  DELETING_BANK,
  DELETING_BANK_FAILURE,
  RECEIVE_CHECK_NUMBERS,
  RECEIVING_CHECK_NUMBERS,
  RECEIVE_CHECK_NUMBERS_FAILURE,
  CLEAR_BANK_STATE,
  RESET_BANK_META,
  CLEAR_STATE,
  RECEIVE_BANK_STATEMENTS,
  RECEIVING_BANK_STATEMENTS,
  RECEIVE_BANK_STATEMENTS_FAILURE,
  INCREMENT_BANK_COUNT,
  CLEAR_CHECK_NUMBERS
} from '../mutationTypes'
import { checkStatus, getHeaders, parseJSON, buildUrl, buildUrlWithQuery } from '@/utils/api'
import { getBySearchCriteria } from '@/utils/helpers/GetterHelpers/CommonGetterFunctions'
import handleXHRError from '@/utils/helpers/HandleXHRError'
import {
  deleteData,
  increment,
  receiveData,
  receiveMetadata,
  clearLocalState,
  resetMetadata,
  clearState
} from '@/store/modules/helpers/mutationHelpers'

const bankSchema = new schema.Entity('banks')
const bankListSchema = new schema.Array(bankSchema)

// initial State
const state = {
  data: {},
  cache: {},
  count: null,
  shown: null,
  next: null,
  previous: null,
  saving: false,
  saveFailure: null,
  receivingData: false,
  receiveDataFailure: false,
  updating: null,
  updateFailure: null,
  deleting: false,
  deleteFailure: null,
  startingCheckNumbers: {},
  receivingCheckNumbers: false,
  receivingCheckNumbersFailure: null,
  statements: {},
  receivingBankStatements: false,
  receivingBankStatementsFailure: null
}

const actions = {
  fetchBanks (store, isNextSet = false) {
    const urlToFetchFrom = () => {
      if (isNextSet) {
        const nextUrl = store.state.next
        return buildUrl(null, null, nextUrl)
      }
      const instanceUrl = this.getters.getActiveInstance.url
      return buildUrl('banks', null, instanceUrl)
    }
    this.commit(RECEIVING_BANKS, true)
    return http.get(urlToFetchFrom(), { headers: getHeaders(this.getters.getToken) })
      .then(checkStatus)
      .then(parseJSON)
      .then(r => {
        this.commit(RECEIVING_BANKS, false)
        if (r.hasOwnProperty('results')) {
          const { results, next, previous, count } = r
          const { entities, result } = normalize(results, bankListSchema)
          if (!result || (Array.isArray(result) && !result.length)) return this.commit(RECEIVE_BANKS_FAILURE, 'Error normalizing response')
          this.commit(RECEIVE_BANKS, { data: entities.banks })
          this.commit(RECEIVE_BANK_METADATA, { next, previous, count, result: result.length })
          return entities
        }
      })
      .catch(e => {
        this.commit(RECEIVING_BANKS, false)
        this.commit(RECEIVE_BANKS_FAILURE, handleXHRError(e))
      })
  },
  fetchBanksWithQuery (store, query) {
    const instanceUrl = this.getters.getActiveInstance.url
    const headers = getHeaders(this.getters.getToken)
    this.commit(RECEIVING_BANKS, true)
    return http.get(buildUrlWithQuery('banks', query, instanceUrl), { headers })
      .then(checkStatus)
      .then(parseJSON)
      .then(r => {
        this.commit(RECEIVING_BANKS, false)
        this.commit(RESET_BANK_META)
        if (r.hasOwnProperty('results')) {
          const { results, next, previous, count } = r
          const { entities, result } = normalize(results, bankListSchema)
          if (!result || (Array.isArray(result) && !result.length)) return this.commit(RECEIVE_BANKS_FAILURE, 'Error normalizing response')
          this.commit(RECEIVE_BANKS, { data: entities.banks })
          this.commit(RECEIVE_BANK_METADATA, { next, previous, count, result: result.length })
          return { query, count, entities }
        }
      })
      .catch((e) => {
        this.commit(RECEIVING_BANKS, false)
        this.commit(RECEIVE_BANKS_FAILURE, handleXHRError(e))
      })
  },
  fetchBankOptions (store, query) {
    const instanceUrl = this.getters.getActiveInstance.url
    const headers = getHeaders(this.getters.getToken)
    this.commit(RECEIVING_BANKS, true)
    return http({ url: buildUrl('banks', query || null, instanceUrl), headers, method: 'options' })
      .then(checkStatus)
      .then(parseJSON)
      .then(r => {
        this.commit(RECEIVING_BANKS, false)
        if (r.hasOwnProperty('choices')) {
          const { choices } = r
          const { entities, result } = normalize(choices, bankListSchema)
          if (!result || !Array.isArray(result)) return Promise.reject('Error normalizing response')
          this.commit(RECEIVE_BANKS, { data: entities.banks })
          return r
        }
      })
      .catch(e => {
        this.commit(RECEIVING_BANKS, false)
        this.commit(RECEIVE_BANKS_FAILURE, handleXHRError(e))
      })
  },
  fetchBankStatementsByBank (store, id) {
    this.commit(RECEIVING_BANK_STATEMENTS, true)
    if (!id) {
      this.commit(RECEIVING_BANK_STATEMENTS, false)
      this.commit(RECEIVE_BANK_STATEMENTS_FAILURE, 'No Bank ID for Statements')
      return false
    }
    const instanceUrl = this.getters.getActiveInstance.url
    return http.get(buildUrl(`banks/${id}/statements`, null, instanceUrl), { headers: getHeaders(this.getters.getToken) })
      .then(checkStatus)
      .then(response => {
        this.commit(RECEIVING_BANK_STATEMENTS, false)
        const { last, next, statements } = response.body
        if (!statements || !Array.isArray(statements)) return this.commit(RECEIVE_BANK_STATEMENTS_FAILURE, 'Error normalizing response')
        this.commit(RECEIVE_BANK_STATEMENTS, { id, last, next, statements })
        return { id, last, next, statements }
      })
      .catch((e) => {
        this.commit(RECEIVING_BANK_STATEMENTS, false)
        this.commit(RECEIVE_BANK_STATEMENTS_FAILURE, handleXHRError(e))
      })
  },
  fetchCheckNumbers (store, payload) {
    if (payload) {
      const instanceUrl = this.getters.getActiveInstance.url
      const headers = getHeaders(this.getters.getToken)
      const { id, params } = payload
      this.commit(RECEIVING_CHECK_NUMBERS, true)
      return http.get(buildUrl(`banks/${id}/checks`, params || null, instanceUrl), { headers })
        .then(checkStatus)
        .then(parseJSON)
        .then(r => {
          this.commit(RECEIVING_CHECK_NUMBERS, false)
          if (r) {
            this.commit(RECEIVE_CHECK_NUMBERS, { id, data: r })
            return r
          }
        })
        .catch(e => {
          this.commit(RECEIVING_CHECK_NUMBERS, false)
          this.commit(RECEIVE_CHECK_NUMBERS_FAILURE, handleXHRError(e))
        })
    } else {
      this.commit(RECEIVING_CHECK_NUMBERS, false)
      this.commit(RECEIVE_CHECK_NUMBERS_FAILURE, 'No payload argument provided')
      return false
    }
  },
  saveBank (store, payload) {
    const instanceUrl = this.getters.getActiveInstance.url
    const headers = getHeaders(this.getters.getToken)
    if (payload) {
      this.commit(SAVING_BANK, true)
      this.commit(SAVING_BANK_FAILURE, null)
      return http.post(buildUrl('banks', null, instanceUrl), payload, { headers })
        .then(checkStatus)
        .then(parseJSON)
        .then(r => {
          this.commit(SAVING_BANK, false)
          if (!r) return this.commit(SAVING_BANK_FAILURE, 'Error saving bank')
          const { entities, result } = normalize(r, bankSchema)
          if (!result || (Array.isArray(result) && !result.length)) return this.commit(RECEIVE_BANKS_FAILURE, 'Error normalizing response')
          this.commit(RECEIVE_BANKS, { data: entities.banks })
          this.commit(INCREMENT_BANK_COUNT, 1)
          return r
        })
        .catch(e => {
          this.commit(SAVING_BANK, false)
          this.commit(SAVING_BANK_FAILURE, handleXHRError(e))
        })
    } else {
      this.commit(SAVING_BANK, false)
      this.commit(SAVING_BANK_FAILURE, 'Error saving bank')
    }
  },
  updateBank (store, payload) {
    const { id, data } = payload
    const instanceUrl = this.getters.getActiveInstance.url
    const headers = getHeaders(this.getters.getToken)
    this.commit(UPDATING_BANK, id)
    this.commit(UPDATING_BANK_FAILURE, { id: '', err: '' })
    return http.patch(buildUrl(`banks/${id}`, null, instanceUrl), data, { headers })
      .then(r => {
        this.commit(UPDATING_BANK, null)
        if (r && r.status === 200) {
          const { entities, result } = normalize(r.body, bankSchema)
          if (!result || (Array.isArray(result) && !result.length)) return this.commit(RECEIVE_BANKS_FAILURE, 'Error normalizing response')
          this.commit(RECEIVE_BANKS, { data: entities.banks })
          return r.body
        } else {
          this.commit(RECEIVE_BANKS_FAILURE, r ? r.statusText : 'Failed to update bank')
        }
      })
      .catch(e => {
        this.commit(UPDATING_BANK, null)
        this.commit(UPDATING_BANK_FAILURE, { id, err: handleXHRError(e) })
      })
  },
  deleteBank (store, payload) {
    const instanceUrl = this.getters.getActiveInstance.url
    const headers = getHeaders(this.getters.getToken)
    if (payload) {
      this.commit(DELETING_BANK, true)
      this.commit(DELETING_BANK_FAILURE, { id: '', err: '' })
      return http.delete(buildUrl(`banks/${payload}`, null, instanceUrl), { headers })
        .then(checkStatus)
        .then(() => {
          this.commit(DELETE_BANK, payload)
          this.commit(DELETING_BANK, false)
          return payload
        })
        .catch(e => {
          console.log(e)
          this.commit(DELETING_BANK_FAILURE, { id: payload, err: e || 'Error deleting bank' })
        })
    } else {
      this.commit(DELETING_BANK, false)
    }
  },
  clearBankState (store, clearCache = false) {
    return this.commit(CLEAR_BANK_STATE, clearCache)
  },
  clearCheckNumbers (store) {
    this.commit(CLEAR_CHECK_NUMBERS)
  }
}

const getters = {
  getBanks: (state) => {
    return state.data ? Object.values(state.data).map(v => Object.assign({}, v)) : []
  },
  getAllBanks: (state) => {
    return state.cache ? Object.values(state.cache).map(v => Object.assign({}, v)) : []
  },
  getBankById: (state) => (id) => {
    if (state.data && state.data[id]) {
      return state.data[id]
    } else if (state.cache && state.cache[id]) {
      return state.cache[id]
    } else {
      return {}
    }
  },
  getDefaultBank: (state) => {
    return Object.values(state.cache).find(v => v.active) || {}
  },
  getBankByCompanyId: (state, rootState) => (companyId) => {
    if (!companyId) return null
    const company = rootState.getCompanyById(companyId)
    if (company && company.bank) {
      if (typeof company.bank === 'object') {
        return state.data[company.bank.id] || state.cache[company.bank.id]
      }
      return state.data[company.bank] || state.cache[company.bank]
    }
  },
  getBankByBatchId: (state, rootState) => (batchId) => {
    if (!batchId) return null
    const batch = rootState.getBatchById(batchId)
    return (batch && batch.bank) ? state.cache[batch.bank] : {}
  },
  getBankMetadata: (state) => {
    const { shown, count } = state
    if (!shown) return { count: 0, shown: 0 }
    return { count, shown }
  },
  getBankStatementsByBank: (state) => (id) => {
    if (state.statements && state.statements[id]) {
      return state.statements[id]
    } else {
      return {}
    }
  },
  getBanksBySearchCriteria: (state) => (query) => {
    return getBySearchCriteria(state.data, query)
  },
  getBankFetchStatus: (state) => {
    return state.receivingData
  },
  getBankSaveStatus: (state) => {
    return state.saving
  },
  getBankSaveFailure: (state) => {
    return state.saveFailure
  },
  getBankUpdateStatus: (state) => {
    return state.updating
  },
  getBankUpdateFailure: (state) => {
    return state.updateFailure
  },
  getBankDeleteStatus: (state) => {
    return state.deleting
  },
  getBankDeleteFailure: (state) => {
    return state.deleteFailure
  },
  getCheckNumbers: (state) => {
    return state.startingCheckNumbers
  },
  getStartingCheckNumberByBankId: (state) => (bankId) => {
    return state.startingCheckNumbers[bankId]
  },
  getBankStatements: (state) => {
    return state.statements
  }
}

const mutations = {
  [RECEIVE_BANKS] (state, results) {
    receiveData(state, results)
  },
  [RECEIVING_BANKS] (state, payload) {
    state.receivingData = payload
  },
  [RECEIVE_BANKS_FAILURE] (state, error) {
    state.receiveDataFailure = error
  },
  [SAVING_BANK] (state, payload) {
    state.saving = payload
  },
  [SAVING_BANK_FAILURE] (state, payload) {
    state.saveFailure = payload
  },
  [UPDATING_BANK] (state, payload) {
    state.updating = payload
  },
  [UPDATING_BANK_FAILURE] (state, payload) {
    const { id, err } = payload
    state.updateFailure = { id, err }
  },
  [DELETE_BANK] (state, payload) {
    deleteData(state, payload)
  },
  [DELETING_BANK] (state, payload) {
    state.deleting = payload
  },
  [DELETING_BANK_FAILURE] (state, payload) {
    const { id, err } = payload
    state.deleteFailure = { id, err }
  },
  [RECEIVE_CHECK_NUMBERS] (state, payload) {
    if (payload) {
      let temp = {}
      temp[payload.id] = payload.data
      state.startingCheckNumbers = Object.assign({}, state.startingCheckNumbers, temp)
    }
  },
  [RECEIVING_CHECK_NUMBERS] (state, payload) {
    state.receivingCheckNumbers = payload
  },
  [RECEIVE_CHECK_NUMBERS_FAILURE] (state, payload) {
    state.receivingCheckNumbersFailure = payload
  },
  [RECEIVING_BANK_STATEMENTS] (state, payload) {
    state.receivingBankStatements = payload
  },
  [RECEIVE_BANK_STATEMENTS] (state, results) {
    const { id, last, next, statements } = results
    if (!(statements && Array.isArray(statements))) return state.statements[id]
    state.statements[id] = Object.assign({}, {
      last: last,
      next: next,
      statements: statements
    })
  },
  [RECEIVE_BANK_STATEMENTS_FAILURE] (state, payload) {
    state.receivingBankStatementsFailure = payload
  },
  [INCREMENT_BANK_COUNT] (state, payload) {
    increment(state, payload)
  },
  [RECEIVE_BANK_METADATA] (state, payload) {
    receiveMetadata(state, payload)
  },
  [CLEAR_CHECK_NUMBERS] (state) {
    state.startingCheckNumbers = Object.assign({})
  },
  [CLEAR_BANK_STATE] (state, payload) {
    clearLocalState(state, payload)
  },
  [RESET_BANK_META] (state) {
    resetMetadata(state)
  },
  [CLEAR_STATE] (state) {
    clearState(state)
  }
}

export default {
  state,
  actions,
  getters,
  mutations
}
